/* eslint-disable @typescript-eslint/no-var-requires */
import { ReactNode, useContext, useEffect, useState } from 'react';
import { Radio } from 'antd';
import Icon from 'components/icons';
import { AccountsContext } from 'contexts/accounts';
import {
  generateCloseToExpirationValues,
  getAccountStatusCount,
  getPricingCount,
  getPricingCountByCode,
  getPricingPlanFilterOptions,
  isAllPricing,
  isCloseToExpiration,
} from '../AccountsFilters/Filters/utils';
import './StatsCardGroup.scss';
import { StatCardContainer } from './styles';
import { getPricingPlanIdentity } from 'contexts/pricingPlans/identity';
import { PRICING_PLAN_CODE } from 'constants/plans_config';
import PricingPlanLabel from '../PricingPlanLabel';
const { theme } = require('constants/styles/theme');

interface StatCard {
  label: ReactNode;
  value: number;
  percentage: number;
  statUp?: boolean;
  checked?: boolean;
  filter?: {
    name: string;
    value: string;
  };
  onFilterChange?: (filterData: StatCard['filter']) => void;
}

const StatCard = ({ label, value, percentage, checked, filter, onFilterChange }: StatCard) => {
  const [displayedValue, setDisplayedValue] = useState(0);

  useEffect(() => {
    setDisplayedValue(0);
    if (!value) return;

    let animationFrame: number;
    let startTimestamp: number;
    const step = (timestamp: number) => {
      if (startTimestamp === undefined) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / 1200, 1);
      setDisplayedValue(Math.floor(progress * value));
      if (progress < 1) animationFrame = window.requestAnimationFrame(step);
    };
    animationFrame = window.requestAnimationFrame(step);
    return () => window.cancelAnimationFrame(animationFrame);
  }, [value]);

  return (
    <StatCardContainer
      className="col-2 card card-body"
      onClick={() => onFilterChange({ name: filter.name, value: filter.value })}
    >
      <div className="d-flex align-items-center">
        <p className="stat-text fw-bolder mb-0">{displayedValue}</p>
        {percentage && (
          <div className={'ms-md-4'}>
            {percentage > 0 ? (
              <Icon icon="ArrowUp" color={theme.default.colors.performance.positive} />
            ) : (
              <Icon icon="ArrowDown" color={theme.default.colors.performance.negative} />
            )}
            <p className="d-none d-md-block mb-0">{percentage}%</p>
          </div>
        )}
      </div>
      <p className="label-user mb-0 text-muted">
        {filter && (
          // Each card's radio gets its own native group (name = filter field + value)
          // instead of sharing `filter.name` (e.g. "pricing_plan") across cards. These
          // filters are independently multi-selectable (see handleFilterChange's
          // multiple:true), so a shared name would let the browser's native radio-group
          // exclusivity silently uncheck a sibling card, e.g. Professional vs Professional
          // LEGACY, on a real click even though both stay selected in application state.
          // `filter.value` alone is not unique across cards (e.g. is_free_trial and
          // close_to_expiration both use "true"), so combine it with `filter.name`.
          <Radio
            checked={checked}
            name={`${filter?.name}_${filter?.value}`}
            value={filter?.value}
          />
        )}
        {label}
      </p>
    </StatCardContainer>
  );
};

const StatsCardGroup = (): JSX.Element => {
  const { filters, counts, actions } = useContext(AccountsContext);
  const [allPricing, setAllPricing] = useState(isAllPricing({ plan: filters.pricing_plan }));
  const [closeToExpiration, setCloseToExpiration] = useState(
    isCloseToExpiration(filters.end_date_from, filters.end_date_to)
  );
  const professionalPlans = getPricingPlanFilterOptions(counts).filter(
    (plan) =>
      plan.code === PRICING_PLAN_CODE.PROFESSIONAL ||
      plan.code === PRICING_PLAN_CODE.LEGACY_PROFESSIONAL
  );

  useEffect(() => {
    const isAll = isAllPricing({ plan: filters.pricing_plan });
    setAllPricing(isAll);
  }, [filters.pricing_plan]);

  useEffect(() => {
    setCloseToExpiration(isCloseToExpiration(filters.end_date_from, filters.end_date_to));
  }, [filters.end_date_from, filters.end_date_to]);

  const handleFilterChange = ({ name, value }) => {
    if (name === 'pricing_plan') {
      const isNotAll = value !== 'pricing_all';
      const isMultipleValue = ['pricing_plan'].includes(name) && isNotAll;
      actions.onChangeFilters({
        name: name,
        value: value,
        multiple: isMultipleValue,
      });
    } else if (name === 'close_to_expiration') {
      let from = '';
      let to = '';
      if (!closeToExpiration) {
        [from, to] = generateCloseToExpirationValues();
      }
      actions.onChangeFilters({
        name: 'end_date_from',
        value: from,
      });
      actions.onChangeFilters({
        name: 'end_date_to',
        value: to,
      });
    } else if (name === 'status') {
      actions.onChangeFilters({
        name: name,
        value: value.toString(),
        multiple: true,
      });
    } else {
      actions.onChangeFilters({
        name: name,
        value: value.toString(),
      });
    }
    actions.clearSelectedItems();
  };

  return (
    <div className="container-fluid stats-card-group px-0">
      <div className="row justify-content-center stats-card-row gx-0">
        <StatCard
          label={'Total accounts'}
          value={getPricingCount({ name: 'all', code: null, legacy: null, count: 0 }, counts)}
          percentage={null}
          checked={allPricing}
          filter={{ name: 'pricing_plan', value: 'pricing_all' }}
          onFilterChange={handleFilterChange}
        />
        <StatCard
          label={'Past Due'}
          value={getAccountStatusCount('past_due', counts)}
          percentage={null}
          checked={filters.status.includes('past_due')}
          filter={{ name: 'status', value: 'past_due' }}
          onFilterChange={handleFilterChange}
        />
        {professionalPlans.map((plan) => (
          <StatCard
            key={getPricingPlanIdentity(plan)}
            label={<PricingPlanLabel name={plan.name} legacy={plan.legacy} />}
            value={getPricingCount(plan, counts)}
            percentage={null}
            checked={filters.pricing_plan.includes(plan.filterValue)}
            filter={{ name: 'pricing_plan', value: plan.filterValue }}
            onFilterChange={handleFilterChange}
          />
        ))}
        <StatCard
          label={'Enterprise'}
          value={getPricingCountByCode({ name: 'Enterprise', code: 'enterprise' }, counts)}
          percentage={null}
          checked={filters.pricing_plan.includes('enterprise')}
          filter={{ name: 'pricing_plan', value: 'enterprise' }}
          onFilterChange={handleFilterChange}
        />
        <StatCard
          label={'Unlimited accounts'}
          value={counts?.license_count?.unlimited}
          percentage={null}
          checked={filters.is_license_unlimited === '1'}
          filter={{ name: 'is_license_unlimited', value: '1' }}
          onFilterChange={handleFilterChange}
        />
        <StatCard
          label={'Free accounts'}
          value={counts?.free_count}
          percentage={null}
          checked={filters.is_free_trial === 'true'}
          filter={{ name: 'is_free_trial', value: 'true' }}
          onFilterChange={handleFilterChange}
        />
        <StatCard
          label={'Expires in 30 days'}
          value={counts?.close_to_expiration_count}
          percentage={null}
          checked={closeToExpiration}
          filter={{ name: 'close_to_expiration', value: 'true' }}
          onFilterChange={handleFilterChange}
        />
      </div>
    </div>
  );
};

export default StatsCardGroup;
